import type { AppEntity, AppField, AppMeta, AppOption } from '@databink/core/api';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { trpc } from './trpc.js';

/**
 * The deploy's metadata for the signed-in person (`meta.app`): entities, fields, options,
 * layouts and branding. Every generic screen is drawn from it, which is why a client's
 * custom field or entity appears without anyone writing a screen for it.
 */

export type { AppEntity, AppField, AppMeta, AppOption };

export const appMetaQuery = trpc.meta.app.queryOptions(undefined, { staleTime: 5 * 60_000 });
export const sessionQuery = trpc.auth.session.queryOptions(undefined, { staleTime: 60_000 });

export function useAppMeta(): AppMeta {
  return useSuspenseQuery(appMetaQuery).data;
}

export function useSession() {
  return useQuery(sessionQuery);
}

/** The owner dashboard is in the menu: it has views and data for them. */
export function hasDashboard(meta: AppMeta): boolean {
  return meta.dashboard.has_data && meta.dashboard.views.length > 0;
}

export function findEntity(meta: AppMeta, id: string): AppEntity | undefined {
  return meta.entities.find((e) => e.id === id);
}

export function useEntity(id: string): AppEntity {
  const meta = useAppMeta();
  const entity = findEntity(meta, id);
  if (!entity) throw new Error(`There is no ${id} here, or you cannot see it.`);
  return entity;
}

export function fieldOf(entity: AppEntity, key: string): AppField | undefined {
  return entity.fields.find((f) => f.key === key);
}

/** Fields that belong on screens: not the platform's bookkeeping columns. */
const HIDDEN = new Set(['id', 'org_id', 'archived_at']);
export function screenFields(entity: AppEntity): AppField[] {
  return entity.fields.filter((f) => !HIDDEN.has(f.key) && f.type !== 'json');
}

/** The fields a list shows: the layout's columns, else the fields marked for lists. */
export function listColumns(
  meta: AppMeta,
  entity: AppEntity,
  requested?: string[] | null,
): AppField[] {
  const keys = requested ?? meta.layouts[entity.id]?.list_columns ?? null;
  const fields = keys
    ? keys.map((k) => fieldOf(entity, k)).filter((f): f is AppField => Boolean(f))
    : entity.fields.filter((f) => f.list && !f.convention);
  const title = fieldOf(entity, entity.title_field);
  const ordered = title && !fields.includes(title) ? [title, ...fields] : fields;
  return ordered.slice(0, 8);
}

/** Sections for a record screen: the layout's, else one section of everything. */
export function sectionsFor(
  meta: AppMeta,
  entity: AppEntity,
): { label: string; fields: AppField[] }[] {
  const layout = meta.layouts[entity.id];
  if (layout) {
    return layout.sections.map((s) => ({
      label: s.label,
      fields: s.fields.map((k) => fieldOf(entity, k)).filter((f): f is AppField => Boolean(f)),
    }));
  }
  const fields = screenFields(entity).filter((f) => !f.convention || f.key === 'tags');
  const custom = fields.filter((f) => f.custom);
  const pulled = entity.id.startsWith('pulled.');
  const lineage = pulled ? fields.filter((f) => LINEAGE.has(f.key)) : [];
  const own = fields.filter((f) => !f.custom && !(pulled && LINEAGE_ALL.has(f.key)));
  return [
    { label: 'Details', fields: own },
    ...(custom.length > 0 ? [{ label: 'Additional details', fields: custom }] : []),
    ...(lineage.length > 0 ? [{ label: 'Where this copy came from', fields: lineage }] : []),
  ];
}

/**
 * Where a copy from the business's own tools came from, shown apart from what it says. The
 * fingerprints the sync keeps for itself are left off screens.
 */
const LINEAGE = new Set([
  'source_id',
  'external_id',
  'source_updated_at',
  'fetched_at',
  'vanished_at',
]);
const LINEAGE_ALL = new Set([...LINEAGE, 'content_hash', 'sync_run_id', 'raw_id']);

/** Options for a select field: its own, or those configured (pipelines, roles), narrowed by the field it depends on. */
export function optionsFor(
  meta: AppMeta,
  field: AppField,
  record?: Record<string, unknown>,
): { value: string; label: string; default?: boolean }[] {
  if (field.options && field.options.length > 0) return field.options;
  const source = field.options_from;
  if (!source) return [];
  const configured = meta.options[source.config_kind] ?? [];
  if (!source.depends_on)
    return configured.map(({ value, label, default: isDefault }) => ({
      value,
      label,
      ...(isDefault && { default: true }),
    }));
  const parent = record?.[source.depends_on];
  const chosen = configured.filter((o) => o.value === parent);
  return (chosen.length > 0 ? chosen : configured).flatMap((o) => o.stages ?? []);
}

/** Entities in the navigation: top-level ones this person can read, in a working order. */
const NAV_ORDER = [
  'crm.party',
  'crm.lead',
  'crm.opportunity',
  'crm.activity',
  'crm.quote',
  'erp.sales_order',
  'erp.invoice',
  'erp.payment',
  'erp.item',
  'erp.tax_code',
  'pulled.job',
  'pulled.estimate',
  'pulled.invoice',
  'pulled.payment',
  'pulled.customer',
  'pulled.technician',
  'pulled.appointment',
  'pulled.time_entry',
  'pulled.material_line',
  'pulled.status_change',
  'pulled.source',
];
export function navEntities(meta: AppMeta): AppEntity[] {
  const top = meta.entities.filter((e) => e.parent === null && !e.id.startsWith('platform.'));
  const rank = (e: AppEntity) => {
    const i = NAV_ORDER.indexOf(e.id);
    return i === -1 ? NAV_ORDER.length : i;
  };
  return [...top].sort((a, b) => rank(a) - rank(b) || a.plural.localeCompare(b.plural));
}

/** Child entities shown on a parent's screen (a customer's contacts, a payment's allocations). */
export function childEntities(meta: AppMeta, entity: AppEntity): AppEntity[] {
  return meta.entities.filter((e) => e.parent?.entity === entity.id && e.id !== entity.lines);
}

export const GROUP_LABELS: Record<string, string> = {
  crm: 'Sales',
  erp: 'Operations',
  pulled: 'From your tools',
  admin: 'Administration',
};
